import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '../ui/card';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '../ui/select';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Alert, AlertDescription } from '../ui/alert';
import { Spinner } from '../ui/spinner';
import { Table, TableHeader, TableBody, TableRow, TableCell } from '../ui/table';
import { SortableHead } from './TableControls';
import { gameDepartmentAPI } from '../../services/api';
import useBrandChange from './useBrandChange';
import LaunchCurveChart from './LaunchCurveChart';
import CheckpointTable from './CheckpointTable';
import GameTitleButton from './GameTitleButton';
import ProviderPreReleases from './ProviderPreReleases';
import { ToggleGroup, ToggleItem } from '../ui/toggle';
import { Multiple, metricFormatters } from './BenchmarkMultiple';
import { toRequestRange, todayUtc } from './performanceData';
import { comparisonRange, periodLabel, phaseMarkers, preReleasePeriod, releaseTypeMeta } from './releaseData';
import {
  BENCH_METRICS, HORIZONS, bandSeries, isPreRelease, scorecardRows, sortScorecard,
} from './launchCurves';

// The benchmark covers the whole tracked history: a shorter period would
// shrink the groups every game is compared with. The backend leaves out
// untracked games (released before tracking began), so an early start date
// costs nothing.
const ALL_HISTORY_FROM = '2015-01-01';

const VIEWS = [
  { key: 'game',     label: 'Game vs benchmark' },
  { key: 'provider', label: 'By provider' },
];

const CATEGORY_SCOPES = [
  { key: 'category', label: 'Same category' },
  { key: 'all',      label: 'All categories' },
];

const PROVIDER_SCOPES = [
  { key: 'all',      label: 'All providers' },
  { key: 'provider', label: 'Same provider' },
];

// How many releases each benchmark group holds: every tracked one, or
// at most the N most recent (before or after the game being scored) — fewer
// when that's all there is.
const RECENCY = [
  { key: 'all', label: 'All releases' },
  { key: '3',   label: 'Up to 3 releases' },
  { key: '5',   label: 'Up to 5 releases' },
  { key: '10',  label: 'Up to 10 releases' },
];

// The game's pre-release dates and length, with its VIP week underneath.
function PeriodCell({ curve }) {
  const period = preReleasePeriod(curve);
  if (!period) return <TableCell className="text-ink-mute">—</TableCell>;
  return (
    <TableCell className="whitespace-nowrap">
      <span>{periodLabel(period.start, period.end)}</span>
      <span className="text-ink-mute"> · {period.days}d</span>
      {period.vip && (
        <p className="text-[11px] text-ink-dim">VIP: {periodLabel(period.vip.start, period.vip.end)} · {period.vip.days}d</p>
      )}
    </TableCell>
  );
}

function ScorecardRow({ row, fmt, selected, onSelect }) {
  const { curve } = row;
  const type = releaseTypeMeta(curve.release_type);
  return (
    <TableRow onClick={() => onSelect(curve.unique_game_id)} className={`cursor-pointer ${selected ? 'bg-surface-2' : ''}`}>
      <TableCell className="min-w-[160px]">
        <GameTitleButton
          title={curve.game_title}
          onActivate={() => onSelect(curve.unique_game_id)}
          label={`Show ${curve.game_title} on the chart`}
        />
      </TableCell>
      <TableCell className="text-ink-dim">{curve.provider}</TableCell>
      <TableCell><Badge variant={type.variant} className="whitespace-nowrap">{type.label}</Badge></TableCell>
      <PeriodCell curve={curve} />
      <TableCell className="text-right">{row.age}</TableCell>
      <TableCell className="text-right">{row.value == null ? '—' : fmt(row.value)}</TableCell>
      <TableCell className="text-right" onClick={e => e.stopPropagation()}>
        <Multiple value={row.vsNormal} group={row.normal} fmt={fmt} age={row.age} />
      </TableCell>
      <TableCell className="text-right" onClick={e => e.stopPropagation()}>
        <Multiple value={row.vsPeers} group={row.peers} fmt={fmt} age={row.age} />
      </TableCell>
      <TableCell className="text-right whitespace-nowrap">{row.rank ? `${row.rank.position} of ${row.rank.of}` : '—'}</TableCell>
    </TableRow>
  );
}

export default function PreReleaseBenchmarkReport() {
  const navigate = useNavigate();
  const [curves, setCurves] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [metric, setMetric] = useState(BENCH_METRICS[0].key);
  const [horizon, setHorizon] = useState(14);
  const [categoryScope, setCategoryScope] = useState('category');
  const [providerScope, setProviderScope] = useState('all');
  const [recency, setRecency] = useState('5');
  const benchmark = useMemo(() => ({
    sameCategory: categoryScope === 'category',
    sameProvider: providerScope === 'provider',
    lastN: recency === 'all' ? null : Number(recency),
  }), [categoryScope, providerScope, recency]);
  const [view, setView] = useState('game');
  const [selectedId, setSelectedId] = useState(null);
  const [sortCol, setSortCol] = useState('release_date');
  const [sortDir, setSortDir] = useState('desc');

  const activeBrand = useBrandChange(() => {
    setCurves([]);
    setSelectedId(null);
  });

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError('');
    const today = todayUtc().toISOString().slice(0, 10);
    gameDepartmentAPI.getReleaseCurves(toRequestRange({ from: ALL_HISTORY_FROM, to: today }))
      .then(res => { if (!cancelled) setCurves(res.data?.curves || []); })
      .catch(() => { if (!cancelled) setError('Failed to load release curves.'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [activeBrand]);

  const rows = useMemo(() => scorecardRows(curves, { metric, horizon, ...benchmark }), [curves, metric, horizon, benchmark]);
  const sortedRows = useMemo(() => sortScorecard(rows, sortCol, sortDir), [rows, sortCol, sortDir]);

  function toggleSort(col) {
    if (sortCol === col) {
      setSortDir(d => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortCol(col);
      setSortDir('desc');
    }
  }
  const sortProps = { sortCol, sortDir, onSort: toggleSort };
  const counts = useMemo(() => ({
    pre: curves.filter(c => isPreRelease(c.release_type)).length,
    standard: curves.filter(c => c.release_type === 'standard').length,
  }), [curves]);

  const selected = curves.find(c => c.unique_game_id === selectedId) || rows[0]?.curve || null;
  const chartData = useMemo(() => (
    selected ? bandSeries(curves, selected, { metric, horizon, ...benchmark }) : []
  ), [curves, selected, metric, horizon, benchmark]);

  const { fmt, axisFmt } = metricFormatters(metric);
  const metricLabel = BENCH_METRICS.find(m => m.key === metric).label;

  function openPerformance(curve) {
    const game = { unique_game_id: curve.unique_game_id, game_title: curve.game_title, provider: curve.provider };
    navigate('/game-department/performance', { state: { games: [game], range: comparisonRange([curve], todayUtc()) } });
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Pre-release Benchmark</CardTitle>
        <CardDescription>
          How each pre-release — with or without a VIP phase — is doing compared with normal releases and other
          pre-releases at the same age: a pre-release on day 9 is compared with every other game&apos;s first 9 days. Compare
          with every tracked release or only the most recent ones, from any provider or only its
          own. Values are cumulative since
          release. Expected GGR is turnover × (1 − RTP): what the game should have earned, without the luck that
          dominates actual GGR over a few days.
        </CardDescription>
      </CardHeader>
      <CardContent>
        {error && <Alert variant="error" className="mb-4"><AlertDescription>{error}</AlertDescription></Alert>}

        <ToggleGroup value={view} onChange={setView} className="mb-4">
          {VIEWS.map(v => <ToggleItem key={v.key} value={v.key}>{v.label}</ToggleItem>)}
        </ToggleGroup>

        <div className="flex flex-wrap items-end gap-3 mb-4">
          <div>
            <label className="text-[11px] text-ink-dim mb-1 block">Metric</label>
            <Select value={metric} onValueChange={setMetric}>
              <SelectTrigger className="w-[170px]"><SelectValue /></SelectTrigger>
              <SelectContent>
                {BENCH_METRICS.map(m => <SelectItem key={m.key} value={m.key}>{m.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-[11px] text-ink-dim mb-1 block">Compare up to</label>
            <Select value={String(horizon)} onValueChange={v => setHorizon(Number(v))}>
              <SelectTrigger className="w-[130px]"><SelectValue /></SelectTrigger>
              <SelectContent>
                {HORIZONS.map(h => <SelectItem key={h} value={String(h)}>Day {h}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <span className="text-[11px] text-ink-dim ml-auto">
            {counts.pre} pre-releases · {counts.standard} normal releases
          </span>
        </div>

        {view === 'game' && (
        <div className="flex flex-wrap items-end gap-3 mb-4">
          <span className="h-10 flex items-center text-[11px] font-bold uppercase tracking-wide text-ink-dim">Compare with</span>
          <div>
            <label className="text-[11px] text-ink-dim mb-1 block">Category</label>
            <Select value={categoryScope} onValueChange={setCategoryScope}>
              <SelectTrigger className="w-[150px]"><SelectValue /></SelectTrigger>
              <SelectContent>
                {CATEGORY_SCOPES.map(c => <SelectItem key={c.key} value={c.key}>{c.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-[11px] text-ink-dim mb-1 block">Provider</label>
            <Select value={providerScope} onValueChange={setProviderScope}>
              <SelectTrigger className="w-[150px]"><SelectValue /></SelectTrigger>
              <SelectContent>
                {PROVIDER_SCOPES.map(p => <SelectItem key={p.key} value={p.key}>{p.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-[11px] text-ink-dim mb-1 block">Releases</label>
            <Select value={recency} onValueChange={setRecency}>
              <SelectTrigger className="w-[160px]"><SelectValue /></SelectTrigger>
              <SelectContent>
                {RECENCY.map(r => <SelectItem key={r.key} value={r.key}>{r.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>
        )}

        {loading ? (
          <div className="flex justify-center py-10"><Spinner size="sm" /></div>
        ) : view === 'provider' ? (
          <ProviderPreReleases
            curves={curves}
            metric={metric}
            metricLabel={metricLabel}
            horizon={horizon}
            onOpenGame={id => { setSelectedId(id); setView('game'); }}
          />
        ) : rows.length === 0 ? (
          <div className="flex items-center justify-center py-10 text-xs text-ink-dim">No pre-releases yet</div>
        ) : (
          <>
            {selected && (
              <>
              <div className="mb-4">
                <div className="flex flex-wrap items-center gap-3 mb-2">
                  <div>
                    <p className="text-sm font-bold text-ink">{selected.game_title}</p>
                    <p className="text-[11px] text-ink-dim">
                      {selected.provider} · {selected.category} · {metricLabel}, cumulative by day since release
                      {preReleasePeriod(selected) && ` · pre-release ${periodLabel(preReleasePeriod(selected).start, preReleasePeriod(selected).end)}`}
                    </p>
                  </div>
                  <Button variant="outline" size="sm" className="ml-auto" onClick={() => openPerformance(selected)}>Open in Game Performance</Button>
                </div>
                <LaunchCurveChart data={chartData} fmt={fmt} axisFmt={axisFmt} markers={phaseMarkers(selected, horizon)} height={240} />
                <div className="mt-3">
                  <CheckpointTable curves={curves} selected={selected} benchmark={benchmark} />
                </div>
              </div>
              </>
            )}

            {/* The game list scrolls inside its own box (header row fixed), so
                the chart and totals above stay in view while picking games. */}
            <div className="border border-white/10 rounded-lg overflow-auto max-h-[420px]">
              <Table>
                <TableHeader className="sticky top-0 z-10 bg-surface-1">
                  <TableRow>
                    <SortableHead col="game_title" label="Game" {...sortProps} />
                    <SortableHead col="provider" label="Provider" {...sortProps} />
                    <SortableHead col="release_type" label="Type" {...sortProps} />
                    <SortableHead col="release_date" label="Pre-release period" {...sortProps} />
                    <SortableHead col="age" label="Compared at day" right {...sortProps} />
                    <SortableHead col="value" label={metricLabel} right {...sortProps} />
                    <SortableHead col="vsNormal" label="vs normal releases" right {...sortProps} />
                    <SortableHead col="vsPeers" label="vs other pre-releases" right {...sortProps} />
                    <SortableHead col="rank" label="Rank in pre-releases" right {...sortProps} />
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {sortedRows.map(r => (
                    <ScorecardRow
                      key={r.curve.unique_game_id}
                      row={r}
                      fmt={fmt}
                      selected={selected?.unique_game_id === r.curve.unique_game_id}
                      onSelect={setSelectedId}
                    />
                  ))}
                </TableBody>
              </Table>
            </div>
            <p className="text-[11px] text-ink-dim mt-2">
              &quot;vs normal releases&quot; and &quot;vs other pre-releases&quot; divide the game&apos;s value by the average of
              that group at the same day (1.50× = 50% better); the number in brackets is how many games the group holds —
              hover it to see them, since one hit can pull an average up a lot.
              &quot;Up to N releases&quot; takes the N most recent releases — before or after the game — that
              are already that old, or fewer when that&apos;s all there is; a younger one is skipped for that day and the next
              most recent takes its place. Games nobody played by that day are left out — most likely they were never live.
              &quot;Same category&quot; keeps comparisons like for like (e.g. slots vs slots), so small live tables and niche
              games don&apos;t pull the averages down.
              The chart follows one fixed group per line — the releases that have completed all {horizon} days — so the
              lines only rise; the table compares each game at its own age, which can include newer releases.
              — means no game qualifies. Normal releases are games released after release
              tracking began without a pre-release.
            </p>
          </>
        )}
      </CardContent>
    </Card>
  );
}
